import { Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy } from 'react'

import MainLayout from '@/layouts/MainLayout'
import AuthLayout from '@/layouts/AuthLayout'
import AdminLayout from '@/admin/layouts/AdminLayout'
import DriverLayout from '@/driver/layouts/DriverLayout'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
// import { useAuth as useMainAuth } from '@/hooks/useAuth'
import { useAuth as useAdminAuth } from '@/admin/hooks/useAuth'
import { canAccessAdminPanel } from '@/admin/utils/auth'

// Lazy load pages for code splitting
const HomePage = lazy(() => import('@/pages/Home'))
const AboutPage = lazy(() => import('@/pages/About'))
const ServicesPage = lazy(() => import('@/pages/Services'))
const AboutOrganizationPage = lazy(() => import('@/pages/AboutOrganization'))
const AboutVisionMissionPage = lazy(() => import('@/pages/AboutVisionMission'))
const AboutCoreValuesPage = lazy(() => import('@/pages/AboutCoreValues'))
const AboutApproachMethodologyPage = lazy(() => import('@/pages/AboutApproachMethodology'))
const AboutOverviewPage = lazy(() => import('@/pages/AboutOverview'))
const AboutLeadershipPage = lazy(() => import('@/pages/AboutLeadership'))
const AboutApproachPage = lazy(() => import('@/pages/AboutApproach'))
const AboutIndustriesPage = lazy(() => import('@/pages/AboutIndustries'))
const AboutSuccessStoriesPage = lazy(() => import('@/pages/AboutSuccessStories'))
const AboutCertificationsPage = lazy(() => import('@/pages/AboutCertifications'))
const AboutPartnersPage = lazy(() => import('@/pages/AboutPartners'))
const WhatWeDoPage = lazy(() => import('@/pages/WhatWeDo'))
const ServiceDetailPage = lazy(() => import('@/pages/ServiceDetail'))
const ServiceManagementConsultingPage = lazy(() => import('@/pages/ServiceManagementConsulting'))
const ServiceProfessionalTrainingPage = lazy(() => import('@/pages/ServiceProfessionalTraining'))
const ServiceResearchAnalysisPage = lazy(() => import('@/pages/ServiceResearchAnalysis'))
const ServiceAdvisorySupportPage = lazy(() => import('@/pages/ServiceAdvisorySupport'))
const ResourcesPage = lazy(() => import('@/pages/Resources'))
const ExperiencePage = lazy(() => import('@/pages/Experience'))
const OngoingProjectsPage = lazy(() => import('@/pages/OngoingProjects'))
const CompletedProjectsPage = lazy(() => import('@/pages/CompletedProjects'))
const SectorsPage = lazy(() => import('@/pages/Sectors'))
const SectorDetailPage = lazy(() => import('@/pages/SectorDetail'))
const ContactPage = lazy(() => import('@/pages/Contact'))
const GetInvolvedPage = lazy(() => import('@/pages/GetInvolved'))
const SearchPage = lazy(() => import('@/pages/Search'))
const TermsOfUsePage = lazy(() => import('@/pages/TermsOfUse'))
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicy'))
const CookiesSettingsPage = lazy(() => import('@/pages/CookiesSettings'))
const EthicsCompliancePage = lazy(() => import('@/pages/EthicsCompliance'))
const RFQPage = lazy(() => import('@/pages/RFQ'))
const RFPPage = lazy(() => import('@/pages/RFP'))
const GalleryPage = lazy(() => import('@/pages/Gallery'))
const DetailsPage = lazy(() => import('@/components/shared/DetailsPage'))
const CareerPage = lazy(() => import('@/pages/Career'))
const CareerDetailsPage = lazy(() => import('@/pages/CareerDetails'))
const ProfessionalTrainingAfghanistanPage = lazy(() => import('@/pages/ProfessionalTrainingAfghanistan'))
const FAQPage = lazy(() => import('@/pages/FAQ'))
const FleetPage = lazy(() => import('@/pages/Fleet'))
const PricingPage = lazy(() => import('@/pages/Pricing'))
const BookingPage = lazy(() => import('@/pages/Booking'))
const AirportTransfersService = lazy(() => import('@/pages/services/AirportTransfers'))
const CorporateTravelService = lazy(() => import('@/pages/services/CorporateTravel'))
const WeddingsEventsService = lazy(() => import('@/pages/services/WeddingsEvents'))
const HourlyService = lazy(() => import('@/pages/services/HourlyService'))
const PointToPointService = lazy(() => import('@/pages/services/PointToPoint'))
const CityToursService = lazy(() => import('@/pages/services/CityTours'))
const SpecialOccasionsService = lazy(() => import('@/pages/services/SpecialOccasions'))
const RoadShowsService = lazy(() => import('@/pages/services/RoadShows'))
const ExecutiveProtectionService = lazy(() => import('@/pages/services/ExecutiveProtection'))

// Customer pages
const CustomerDashboard = lazy(() => import('@/pages/Customer/Dashboard'))

// Auth pages
const LoginPage = lazy(() => import('@/pages/Auth/Login'))
const RegisterPage = lazy(() => import('@/pages/Auth/Register'))
const ForgotPasswordPage = lazy(() => import('@/pages/Auth/ForgotPassword'))
const AuthSuccessPage = lazy(() => import('@/pages/Auth/Success'))

// Admin pages - using new admin structure
const AdminLogin = lazy(() => import('@/admin/pages/Login'))
const AdminDashboard = lazy(() => import('@/admin/pages/Dashboard'))
const AdminBookings = lazy(() => import('@/admin/pages/BookingManagement'))
const AdminDrivers = lazy(() => import('@/admin/pages/DriverManagement'))
const AdminFleet = lazy(() => import('@/admin/pages/FleetManagement'))
const AdminCustomers = lazy(() => import('@/admin/pages/CustomerManagement'))
const AdminExtras = lazy(() => import('@/admin/pages/ExtrasManagement'))
const AdminReports = lazy(() => import('@/admin/pages/ReportsAnalytics'))
const AdminContent = lazy(() => import('@/admin/pages/ContentManagement'))
const AdminServices = lazy(() => import('@/admin/pages/Services'))
const AdminCareer = lazy(() => import('@/admin/pages/Career'))
const AdminUsers = lazy(() => import('@/admin/pages/Users'))
const AdminNews = lazy(() => import('@/admin/pages/News'))
const AdminGallery = lazy(() => import('@/admin/pages/Gallery'))
const AdminProjects = lazy(() => import('@/admin/pages/Projects'))
const AdminEvents = lazy(() => import('@/admin/pages/Events'))
const AdminAnalytics = lazy(() => import('@/admin/pages/Analytics'))
const AdminActivity = lazy(() => import('@/admin/pages/Activity'))
const AdminSettings = lazy(() => import('@/admin/pages/SettingsConfiguration'))
const ApiSettings = lazy(() => import('@/admin/pages/ApiSettings'))
const AdminAbout = lazy(() => import('@/admin/pages/About'))
const AdminTeamList = lazy(() => import('@/admin/pages/TeamList'))
const AdminPageSettings = lazy(() => import('@/admin/pages/PageSettings'))
const AdminServicesIndex = lazy(() => import('@/admin/pages/ServicesIndex'))
const AdminManagementConsultingServices = lazy(() => import('@/admin/pages/ManagementConsultingServices'))
const AdminProfessionalTrainingServices = lazy(() => import('@/admin/pages/ProfessionalTrainingServices'))
const AdminResearchAnalysisServices = lazy(() => import('@/admin/pages/ResearchAnalysisServices'))
const AdminAdvisorySupportServices = lazy(() => import('@/admin/pages/AdvisorySupportServices'))
const AdminHealthSectorPage = lazy(() => import('@/admin/pages/HealthSectorPage'))
const AdminEducationSectorPage = lazy(() => import('@/admin/pages/EducationSectorPage'))
const AdminWashSectorPage = lazy(() => import('@/admin/pages/WashSectorPage'))
const AdminLivelihoodsSectorPage = lazy(() => import('@/admin/pages/LivelihoodsSectorPage'))
const AdminGenderSectorPage = lazy(() => import('@/admin/pages/GenderSectorPage'))
const AdminHumanitarianSectorPage = lazy(() => import('@/admin/pages/HumanitarianSectorPage'))
const AdminSectorsList = lazy(() => import('@/admin/pages/SectorsList'))
const AdminSectorEdit = lazy(() => import('@/admin/pages/SectorEdit'))
const AdminSectorsOrdering = lazy(() => import('@/admin/pages/SectorsOrdering'))
const AdminExperienceManagement = lazy(() => import('@/admin/pages/Experience/ExperienceManagement'))
const AdminResourcesManagement = lazy(() => import('@/admin/pages/Resources/ResourcesManagement'))
const AdminRFQ = lazy(() => import('@/admin/pages/RFQ'))
const AdminFAQ = lazy(() => import('@/admin/pages/FAQ'))
const AdminPartners = lazy(() => import('@/admin/pages/Partners'))

// Content Management Components
const AdminHomepageContent = lazy(() => import('@/admin/pages/content/HomepageContent'))
const AdminPageContentEditor = lazy(() => import('@/admin/pages/content/PageContentEditor'))
const AdminContentHub = lazy(() => import('@/admin/pages/content/ContentManagementHub'))
const AdminServicesContentManager = lazy(() => import('@/admin/pages/content/ServicesContentManager'))
const AdminFleetContentManager = lazy(() => import('@/admin/pages/content/FleetContentManager'))
const AdminPricingContentManager = lazy(() => import('@/admin/pages/content/PricingContentManager'))
const AdminBookingContentManager = lazy(() => import('@/admin/pages/content/BookingContentManager'))
const AdminAboutContentManager = lazy(() => import('@/admin/pages/content/AboutContentManager'))
const AdminCareersContentManager = lazy(() => import('@/admin/pages/content/CareersContentManager'))
const AdminGalleryContentManager = lazy(() => import('@/admin/pages/content/GalleryContentManager'))
const AdminFAQContentManager = lazy(() => import('@/admin/pages/content/FAQContentManager'))
// Removed unused lazy imports - these components are not used in routes

// Driver pages
const DriverLogin = lazy(() => import('@/driver/pages/Login'))
const DriverDashboard = lazy(() => import('@/driver/pages/Dashboard'))
const DriverBookings = lazy(() => import('@/driver/pages/MyBookings'))
const DriverEarnings = lazy(() => import('@/driver/pages/Earnings'))
const DriverProfile = lazy(() => import('@/driver/pages/Profile'))
const DriverVehicle = lazy(() => import('@/driver/pages/VehicleInfo'))
const DriverAvailability = lazy(() => import('@/driver/pages/Availability'))
const DriverNotifications = lazy(() => import('@/driver/pages/Notifications'))

function App() {
  const { isAuthenticated, user } = useAdminAuth()
  
  console.log('App render - isAuthenticated:', isAuthenticated, 'user:', user)

  return (
    <Suspense fallback={<LoadingSpinner fullScreen />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<MainLayout />}>
          <Route index element={<HomePage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="about/overview" element={<AboutOverviewPage />} />
          <Route path="about/leadership" element={<AboutLeadershipPage />} />
          <Route path="about/our-approach" element={<AboutApproachPage />} />
          <Route path="about/industries" element={<AboutIndustriesPage />} />
          <Route path="about/success-stories" element={<AboutSuccessStoriesPage />} />
          <Route path="about/certifications" element={<AboutCertificationsPage />} />
          <Route path="about/partners" element={<AboutPartnersPage />} />
          <Route path="about/organization" element={<AboutOrganizationPage />} />
          <Route path="about/vision-mission" element={<AboutVisionMissionPage />} />
          <Route path="about/core-values" element={<AboutCoreValuesPage />} />
          <Route path="about/approach-methodology" element={<AboutApproachMethodologyPage />} />
          
            <Route path="services" element={<ServicesPage />} />
            <Route path="services/:slug" element={<ServiceDetailPage />} />
            <Route path="service/management-consulting" element={<ServiceManagementConsultingPage />} />
            <Route path="service/professional-training" element={<ServiceProfessionalTrainingPage />} />
            <Route path="service/research-analysis" element={<ServiceResearchAnalysisPage />} />
            <Route path="service/advisory-support" element={<ServiceAdvisorySupportPage />} />
            <Route path="what-we-do" element={<WhatWeDoPage />} />
            <Route path="experience" element={<ExperiencePage />} />
            <Route path="projects/ongoing" element={<OngoingProjectsPage />} />
            <Route path="projects/completed" element={<CompletedProjectsPage />} />
            <Route path="sectors" element={<SectorsPage />} />
            <Route path="sectors/:slug" element={<SectorDetailPage />} />
            <Route path="resources" element={<ResourcesPage />} />
            <Route path="search" element={<SearchPage />} />
            <Route path="contact" element={<ContactPage />} />
          <Route path="get-involved" element={<GetInvolvedPage />} />
          <Route path="terms-of-use" element={<TermsOfUsePage />} />
          <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="cookies-settings" element={<CookiesSettingsPage />} />
          <Route path="ethics-compliance" element={<EthicsCompliancePage />} />
          <Route path="rfq" element={<RFQPage />} />
          <Route path="rfp" element={<RFPPage />} />
          <Route path="gallery" element={<GalleryPage />} />
          <Route path="gallery/:slug" element={<DetailsPage />} />
          <Route path="career" element={<CareerPage />} />
          <Route path="career/:id" element={<CareerDetailsPage />} />
          <Route path="professional-training-afghanistan" element={<ProfessionalTrainingAfghanistanPage />} />
          <Route path="faq" element={<FAQPage />} />
          <Route path="fleet" element={<FleetPage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="booking" element={<BookingPage />} />
          <Route path="services/airport-transfers" element={<AirportTransfersService />} />
          <Route path="services/corporate" element={<CorporateTravelService />} />
          <Route path="services/weddings" element={<WeddingsEventsService />} />
          <Route path="services/hourly" element={<HourlyService />} />
          <Route path="services/point-to-point" element={<PointToPointService />} />
          <Route path="services/city-tours" element={<CityToursService />} />
          <Route path="services/special-occasions" element={<SpecialOccasionsService />} />
          <Route path="services/road-shows" element={<RoadShowsService />} />
          <Route path="services/executive-protection" element={<ExecutiveProtectionService />} />
        </Route>

        {/* Auth Routes */}
        <Route path="/auth" element={<AuthLayout />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="success" element={<AuthSuccessPage />} />
        </Route>

        {/* Customer Dashboard Route */}
        <Route path="/dashboard" element={<CustomerDashboard />} />

        {/* Admin Login Route */}
        <Route path="/admin/login" element={<AdminLogin />} />

        {/* Protected Admin Routes */}
        <Route 
          path="/admin" 
          element={
            isAuthenticated && canAccessAdminPanel(user) ? (
              <AdminLayout />
            ) : (
              <Navigate to="/admin/login" replace />
            )
          }
        >
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="drivers" element={<AdminDrivers />} />
          <Route path="fleet" element={<AdminFleet />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="extras" element={<AdminExtras />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="content" element={<AdminContent />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="api-settings" element={<ApiSettings />} />
          <Route path="users" element={<AdminUsers />} />
          
          {/* Original NGO Routes (keep for now) */}
          <Route path="services" element={<AdminServices />} />
          <Route path="career" element={<AdminCareer />} />
          <Route path="news" element={<AdminNews />} />
          <Route path="gallery" element={<AdminGallery />} />
          <Route path="projects" element={<AdminProjects />} />
          <Route path="events" element={<AdminEvents />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="activity" element={<AdminActivity />} />
          <Route path="about" element={<Navigate to="/admin/about/overview" replace />} />
          <Route path="about/:section" element={<AdminAbout />} />
          <Route path="about/team/list" element={<AdminTeamList />} />
          <Route path="page-settings" element={<AdminPageSettings />} />
          <Route path="services" element={<AdminServicesIndex />} />
          <Route path="services/management-consulting" element={<AdminManagementConsultingServices />} />
          <Route path="services/professional-training" element={<AdminProfessionalTrainingServices />} />
          <Route path="services/research-analysis" element={<AdminResearchAnalysisServices />} />
          <Route path="services/advisory-support" element={<AdminAdvisorySupportServices />} />
          <Route path="sectors" element={<AdminSectorsList />} />
          <Route path="sectors/health" element={<AdminHealthSectorPage />} />
          <Route path="sectors/education" element={<AdminEducationSectorPage />} />
          <Route path="sectors/wash" element={<AdminWashSectorPage />} />
          <Route path="sectors/livelihoods" element={<AdminLivelihoodsSectorPage />} />
          <Route path="sectors/gender" element={<AdminGenderSectorPage />} />
          <Route path="sectors/humanitarian" element={<AdminHumanitarianSectorPage />} />
          <Route path="sectors/new" element={<AdminSectorEdit />} />
          <Route path="sectors/edit/:id" element={<AdminSectorEdit />} />
          <Route path="sectors/ordering" element={<AdminSectorsOrdering />} />
          <Route path="experience" element={<AdminExperienceManagement />} />
          <Route path="resources" element={<AdminResourcesManagement />} />
          <Route path="partners" element={<AdminPartners />} />
          <Route path="rfq" element={<AdminRFQ />} />
          <Route path="faq" element={<AdminFAQ />} />
          
          {/* Content Management Routes */}
          <Route path="content" element={<AdminContentHub />} />
          <Route path="content/homepage" element={<AdminHomepageContent />} />
          <Route path="content/universal" element={<AdminPageContentEditor />} />
          <Route path="content/services" element={<AdminServicesContentManager />} />
          <Route path="content/fleet" element={<AdminFleetContentManager />} />
          <Route path="content/pricing" element={<AdminPricingContentManager />} />
          <Route path="content/bookings" element={<AdminBookingContentManager />} />
          <Route path="content/about" element={<AdminAboutContentManager />} />
          <Route path="content/careers" element={<AdminCareersContentManager />} />
          <Route path="content/gallery" element={<AdminGalleryContentManager />} />
          <Route path="content/faq" element={<AdminFAQContentManager />} />
          <Route path="content/service-pages" element={<AdminPageContentEditor />} />
          <Route path="content/booking" element={<AdminPageContentEditor />} />
          <Route path="content/resources" element={<AdminPageContentEditor />} />
          <Route path="content/news-events" element={<AdminPageContentEditor />} />
        </Route>

        {/* Driver Login Route */}
        <Route path="/driver/login" element={<DriverLogin />} />

        {/* Protected Driver Routes */}
        <Route 
          path="/driver/*" 
          element={
            isAuthenticated ? (
              <DriverLayout>
                <Routes>
                  <Route index element={<Navigate to="/driver/dashboard" replace />} />
                  <Route path="dashboard" element={<DriverDashboard />} />
                  <Route path="bookings" element={<DriverBookings />} />
                  <Route path="earnings" element={<DriverEarnings />} />
                  <Route path="profile" element={<DriverProfile />} />
                  <Route path="vehicle" element={<DriverVehicle />} />
                  <Route path="availability" element={<DriverAvailability />} />
                  <Route path="notifications" element={<DriverNotifications />} />
                </Routes>
              </DriverLayout>
            ) : (
              <Navigate to="/admin/login" replace />
            )
          }
        />

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

export default App